// pages/AdminModerationPage.js
import { useState, useEffect } from 'react'
import {
  getFirestore,
  collection,
  query,
  where,
  onSnapshot,
  doc,
  getDoc,
  updateDoc,
  orderBy,
  getDocs
} from 'firebase/firestore'
import { getFunctions, httpsCallable } from 'firebase/functions'
import {
  getStorage,
  ref as storageRef,
  uploadBytes,
  getDownloadURL
} from 'firebase/storage'
import toast from 'react-hot-toast'
import { app } from '../firebase'
import { useAuth } from '../contexts/AuthContext'
import UserService from '../services/UserService'
import ImageGallery from '../components/ImageGallery'
import ConfirmModal from '../components/ConfirmModal'
import SolutionTextareaWithMentions from '../components/contribute/SolutionTextareaWithMentions'
import SolutionWithImageReferences from '../components/SolutionWithImageReferences'
import { getImageReferenceName, generateUniqueReferenceName, normalizeUmlauts } from '../utils/imageMentions'
import EditIcon from '@mui/icons-material/Edit'
import VisibilityIcon from '@mui/icons-material/Visibility'
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'
import SaveIcon from '@mui/icons-material/Save'
import CloseIcon from '@mui/icons-material/Close'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate'
import CancelIcon from '@mui/icons-material/Cancel'
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty'
import PictureAsPdf from '@mui/icons-material/PictureAsPdf'
import RateReviewIcon from '@mui/icons-material/RateReview'
import FilterListIcon from '@mui/icons-material/FilterList'
import AssignmentIcon from '@mui/icons-material/Assignment'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import KnowledgeHistory from '../components/KnowledgeHistory'
import './AdminModerationPage.css'

function AdminModerationPage () {
  const { user } = useAuth()
  const [pendingKnowledge, setPendingKnowledge] = useState([])
  const [loading, setLoading] = useState(true)
  const [selectedItem, setSelectedItem] = useState(null)
  const [processing, setProcessing] = useState(false)
  const [rejectionReason, setRejectionReason] = useState('')
  const [isEditMode, setIsEditMode] = useState(false)
  const [editedData, setEditedData] = useState(null)
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
  const [showSolutionPreview, setShowSolutionPreview] = useState(false)
  const [filterDevice, setFilterDevice] = useState('all')
  const [filterCategory, setFilterCategory] = useState('all')
  const [sortBy, setSortBy] = useState('newest')
  const [deviceDisplayNameMap, setDeviceDisplayNameMap] = useState(new Map()) // Mapping: displayName -> customDisplayName
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: null,
    type: 'warning'
  })

  // Load devices for display name mapping
  useEffect(() => {
    const loadDevices = async () => {
      if (!user || !user.uid) {
        return
      }

      try {
        const companyId = await UserService.getUserCompanyId(user.uid)
        const db = getFirestore(app)
        const devicesRef = collection(db, 'companies', companyId, 'devices')
        const snapshot = await getDocs(devicesRef)
        
        const map = new Map()
        snapshot.docs.forEach(doc => {
          const deviceData = doc.data()
          if (deviceData.displayName) {
            // Verwende customDisplayName falls vorhanden, sonst displayName
            map.set(deviceData.displayName, deviceData.customDisplayName || deviceData.displayName)
          }
        })
        
        setDeviceDisplayNameMap(map)
      } catch (error) {
        console.error('Error loading devices for display name mapping:', error)
      }
    }

    loadDevices()
  }, [user])

  // Real-time Listener für pending knowledge (company-scoped)
  useEffect(() => {
    if (!user) {
      setLoading(false)
      return
    }

    let unsubscribe = () => {}

    const setupListener = async () => {
      try {
        const companyId = await UserService.getUserCompanyId(user.uid)
        const db = getFirestore(app)

        const q = query(
          collection(db, 'companies', companyId, 'knowledge'),
          where('status', '==', 'pending')
        )

        unsubscribe = onSnapshot(q, snapshot => {
          const items = snapshot.docs.map(doc => {
            const itemData = doc.data()
            // ⭐ NEU: Füge customDisplayName hinzu, falls vorhanden
            const displayName = itemData.deviceName
            const customDisplayName = deviceDisplayNameMap.get(displayName) || displayName
            
            return {
              id: doc.id,
              ...itemData,
              deviceDisplayName: customDisplayName // Neues Feld für Anzeige
            }
          })
          setPendingKnowledge(items)
          setLoading(false)
        })
      } catch (error) {
        console.error('Error setting up knowledge listener:', error)
        setLoading(false)
      }
    }

    setupListener()

    return () => unsubscribe()
  }, [user, deviceDisplayNameMap])

  // ⭐ NEU: Aktualisiere selectedItem, wenn Device-Mapping geladen wird
  useEffect(() => {
    if (deviceDisplayNameMap.size > 0 && selectedItem) {
      const displayName = selectedItem.deviceName
      const customDisplayName = deviceDisplayNameMap.get(displayName) || displayName
      
      if (selectedItem.deviceDisplayName !== customDisplayName) {
        setSelectedItem(prev => ({
          ...prev,
          deviceDisplayName: customDisplayName
        }))
      }
    }
  }, [deviceDisplayNameMap, selectedItem?.deviceName])

  // Initialize editedData when modal opens
  useEffect(() => {
    if (selectedItem) {
      // Normalize images: normalize name/filename and ensure referenceName exists
      const normalizedImages = (selectedItem.images || []).map(img => {
        // Normalize name and filename (convert umlauts to ASCII)
        const normalizedName = img.filename ? normalizeUmlauts(img.filename) : (img.name ? normalizeUmlauts(img.name) : '')
        const normalizedFilename = normalizedName || 'media'
        
        // Generate referenceName from normalized filename if missing
        const referenceName = img.referenceName || getImageReferenceName(normalizedFilename)
        
        // Detect mediaType if missing
        let mediaType = img.mediaType
        if (!mediaType) {
          if (normalizedFilename.match(/\.pdf$/i) || img.url?.match(/\.pdf$/i)) {
            mediaType = 'pdf'
          } else if (img.url?.match(/\.(mp4|webm|mov)$/i) || normalizedFilename.match(/\.(mp4|webm|mov)$/i)) {
            mediaType = 'video'
          } else {
            mediaType = 'image'
          }
        }
        
        return {
          ...img,
          name: normalizedFilename,
          filename: normalizedFilename,
          referenceName,
          mediaType
        }
      })
      
      setEditedData({
        ...selectedItem,
        images: normalizedImages
      })
      
      // Also normalize selectedItem for display
      if (normalizedImages.length !== (selectedItem.images || []).length ||
          normalizedImages.some((img, idx) => img.referenceName !== (selectedItem.images?.[idx]?.referenceName))) {
        setSelectedItem({
          ...selectedItem,
          images: normalizedImages
        })
      }
      
      setIsEditMode(false)
      setHasUnsavedChanges(false)
      setShowSolutionPreview(false) // Reset preview state when opening new item
    } else {
      setEditedData(null)
      setIsEditMode(false)
      setHasUnsavedChanges(false)
    }
  }, [selectedItem])

  const handleEditChange = (field, value) => {
    setEditedData(prev => ({
      ...prev,
      [field]: value
    }))
    setHasUnsavedChanges(true)
  }

  const handleCloseModal = () => {
    if (isEditMode && hasUnsavedChanges) {
      setConfirmModal({
        isOpen: true,
        title: 'Ungespeicherte Änderungen',
        message:
          'Sie haben ungespeicherte Änderungen. Möchten Sie das Modal wirklich schließen?',
        onConfirm: () => {
          setSelectedItem(null)
          setHasUnsavedChanges(false)
        },
        type: 'warning'
      })
    } else {
      setSelectedItem(null)
    }
  }

  const handleCancelEdit = () => {
    if (hasUnsavedChanges) {
      setConfirmModal({
        isOpen: true,
        title: 'Ungespeicherte Änderungen',
        message:
          'Sie haben ungespeicherte Änderungen. Möchten Sie den Bearbeitungsmodus wirklich verlassen?',
        onConfirm: () => {
          setIsEditMode(false)
          setHasUnsavedChanges(false)
          // Reset editedData to original
          setEditedData({ ...selectedItem })
        },
        type: 'warning'
      })
    } else {
      setIsEditMode(false)
    }
  }

  // Filter and sort pending knowledge test
  const getFilteredAndSortedKnowledge = () => {
    let filtered = [...pendingKnowledge]

    // Filter by device
    if (filterDevice !== 'all') {
      filtered = filtered.filter(item => item.deviceName === filterDevice)
    }

    // Filter by category
    if (filterCategory !== 'all') {
      filtered = filtered.filter(item => item.category === filterCategory)
    }

    // Sort
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'newest':
          return b.submittedAt?.toDate() - a.submittedAt?.toDate()
        case 'oldest':
          return a.submittedAt?.toDate() - b.submittedAt?.toDate()
        case 'device':
          return a.deviceName.localeCompare(b.deviceName)
        case 'category':
          return a.category.localeCompare(b.category)
        default:
          return 0
      }
    })

    return filtered
  }

  // Get unique devices and categories for filters (mit customDisplayName für Anzeige)
  const uniqueDevices = [
    ...new Set(pendingKnowledge.map(item => item.deviceName))
  ].sort()
  const uniqueCategories = [
    ...new Set(pendingKnowledge.map(item => item.category))
  ].sort()
  
  // Helper: Get display name for device filter dropdown
  const getDeviceDisplayName = (deviceName) => {
    return deviceDisplayNameMap.get(deviceName) || deviceName
  }

  const filteredKnowledge = getFilteredAndSortedKnowledge()

  const handleImageUpload = async event => {
    const files = event.target.files
    if (!files || files.length === 0) return

    setProcessing(true)
    try {
      const companyId = await UserService.getUserCompanyId(user.uid)
      const storage = getStorage(app)
      const uploadedImages = []

      for (let i = 0; i < files.length; i++) {
        const file = files[i]

        // Validate file type (image, video, or PDF)
        const isVideo = file.type.startsWith('video/')
        const isImage = file.type.startsWith('image/')
        const isPDF = file.type === 'application/pdf'

        if (!isImage && !isVideo && !isPDF) {
          toast.error(`${file.name} ist kein gültiges Bild, Video oder PDF`)
          continue
        }

        // Validate file size (5MB for images, 100MB for videos, 50MB for PDFs)
        const maxSize = isVideo ? 100 * 1024 * 1024 : (isPDF ? 50 * 1024 * 1024 : 5 * 1024 * 1024)
        const maxSizeLabel = isVideo ? '100MB' : (isPDF ? '50MB' : '5MB')

        if (file.size > maxSize) {
          toast.error(`${file.name} ist zu groß (max ${maxSizeLabel})`)
          continue
        }

        // Normalize filename (convert umlauts to ASCII)
        const normalizedFileName = normalizeUmlauts(file.name)
        
        // Upload to Firebase Storage
        const timestamp = Date.now()
        const fileName = `${timestamp}_${normalizedFileName}`
        const imageRef = storageRef(
          storage,
          `knowledge-images/${companyId}/${editedData.id}/${fileName}`
        )

        await uploadBytes(imageRef, file, {
          contentType: file.type || 'image/jpeg'
        })
        const url = await getDownloadURL(imageRef)

        // Generate unique reference name
        const existingImages = editedData?.images || []
        const existingReferenceNames = existingImages
          .map(img => img.referenceName || getImageReferenceName(img.filename || img.name || ''))
          .filter(Boolean)
        
        const baseRefName = getImageReferenceName(normalizedFileName)
        const referenceName = generateUniqueReferenceName(baseRefName, existingReferenceNames)

        // Detect media type (image, video, or pdf)
        let mediaType = 'image'
        if (file.type.startsWith('video/')) {
          mediaType = 'video'
        } else if (file.type === 'application/pdf') {
          mediaType = 'pdf'
        }

        uploadedImages.push({
          url,
          name: normalizedFileName,
          filename: normalizedFileName,
          uploadedAt: new Date().toISOString(),
          referenceName,
          mediaType
        })
      }

      // Add new images to existing images
      const currentImages = editedData.images || []
      setEditedData(prev => ({
        ...prev,
        images: [...currentImages, ...uploadedImages]
      }))
      setHasUnsavedChanges(true)

      toast.success(`${uploadedImages.length} Datei(en) hochgeladen!`)
    } catch (error) {
      console.error('Image upload error:', error)
      toast.error('Fehler beim Hochladen: ' + error.message)
    } finally {
      setProcessing(false)
      // Reset file input
      event.target.value = ''
    }
  }

  const handleSaveChanges = async () => {
    if (!editedData) return

    setProcessing(true)
    try {
      const functions = getFunctions(app)
      const updateKnowledge = httpsCallable(functions, 'updateKnowledge')

      // Convert comma-separated strings to arrays
      const componentsArray =
        typeof editedData.components === 'string'
          ? editedData.components
              .split(',')
              .map(s => s.trim())
              .filter(Boolean)
          : editedData.components || []

      const toolsArray =
        typeof editedData.toolsRequired === 'string'
          ? editedData.toolsRequired
              .split(',')
              .map(s => s.trim())
              .filter(Boolean)
          : editedData.toolsRequired || []

      await updateKnowledge({
        knowledgeId: editedData.id,
        updates: {
          problemTitle: editedData.problemTitle,
          problemDescription: editedData.problemDescription,
          solution: editedData.solution,
          errorCode: editedData.errorCode,
          difficulty: editedData.difficulty,
          estimatedTime: editedData.estimatedTime,
          components: componentsArray,
          toolsRequired: toolsArray,
          images: editedData.images
        }
      })

      // Fetch updated version from Firestore
      const companyId = await UserService.getUserCompanyId(user.uid)
      const db = getFirestore(app)
      const updatedDoc = await getDoc(
        doc(db, 'companies', companyId, 'knowledge', editedData.id)
      )

      if (updatedDoc.exists()) {
        const updatedData = {
          id: updatedDoc.id,
          ...updatedDoc.data()
        }

        // Update both selectedItem and editedData with fresh data
        setSelectedItem(updatedData)
        setEditedData(updatedData)
      }

      toast.success('Änderungen wurden gespeichert!')
      setHasUnsavedChanges(false)
      setIsEditMode(false)
    } catch (error) {
      console.error('Save error:', error)
      toast.error('Fehler beim Speichern: ' + error.message)
    } finally {
      setProcessing(false)
    }
  }

  const handleApprove = async item => {
    setConfirmModal({
      isOpen: true,
      title: 'Wissen freigeben',
      message: `Dieses Wissen wirklich freigeben?\n\n"${item.problemTitle}"`,
      onConfirm: () => approveKnowledge(item),
      type: 'success'
    })
  }

  const approveKnowledge = async item => {
    setProcessing(true)
    try {
      const functions = getFunctions(app)
      const approveKnowledge = httpsCallable(
        functions,
        'approveAndEmbedKnowledge'
      )

      await approveKnowledge({
        knowledgeId: item.id
      })

      toast.success('Wissen wurde freigegeben und ist jetzt verfügbar!')
      setSelectedItem(null)
    } catch (error) {
      console.error('Approval error:', error)
      toast.error('Fehler: ' + error.message)
    } finally {
      setProcessing(false)
    }
  }

  const handleReject = async item => {
    if (!rejectionReason.trim()) {
      toast.error('Bitte geben Sie einen Ablehnungsgrund an.')
      return
    }

    setConfirmModal({
      isOpen: true,
      title: 'Wissen ablehnen',
      message: `Dieses Wissen wirklich ablehnen?\n\n"${item.problemTitle}"\n\nGrund: ${rejectionReason}`,
      onConfirm: () => rejectKnowledge(item),
      type: 'danger'
    })
  }

  const rejectKnowledge = async item => {
    setProcessing(true)
    try {
      const functions = getFunctions(app)
      const rejectKnowledge = httpsCallable(functions, 'rejectKnowledge')

      await rejectKnowledge({
        knowledgeId: item.id,
        rejectionReason: rejectionReason.trim()
      })

      toast.success('Wissen wurde abgelehnt.')
      setSelectedItem(null)
      setRejectionReason('')
    } catch (error) {
      console.error('Rejection error:', error)
      toast.error('Fehler: ' + error.message)
    } finally {
      setProcessing(false)
    }
  }

  if (loading) {
    return <div className='loading'>Lade Einreichungen...</div>
  }

  return (
    <div className='admin-moderation-page'>
      <div className='admin-header'>
        <div>
          <h1>Wissens-Moderation</h1>
          <p className='header-subtitle'>Einreichungen prüfen und freigeben</p>
        </div>
        <div className='stats'>
          <div className='stat-badge-admin'>
            <AssignmentIcon className='stat-icon' />
            <span>
              {filteredKnowledge.length} von {pendingKnowledge.length}
            </span>
          </div>
        </div>
      </div>

      {pendingKnowledge.length > 0 && (
        <div className='filter-controls'>
          <div className='filter-controls-header'>
            <FilterListIcon className='filter-icon' />
            <h3>Filter & Sortierung</h3>
          </div>
          <div className='filter-group'>
            <label>Gerät:</label>
            <select
              value={filterDevice}
              onChange={e => setFilterDevice(e.target.value)}
            >
              <option value='all'>
                Alle Geräte ({pendingKnowledge.length})
              </option>
              {uniqueDevices.map(device => (
                <option key={device} value={device}>
                  {getDeviceDisplayName(device)} (
                  {pendingKnowledge.filter(k => k.deviceName === device).length}
                  )
                </option>
              ))}
            </select>
          </div>

          <div className='filter-group'>
            <label>Kategorie:</label>
            <select
              value={filterCategory}
              onChange={e => setFilterCategory(e.target.value)}
            >
              <option value='all'>Alle Kategorien</option>
              {uniqueCategories.map(category => (
                <option key={category} value={category}>
                  {category}
                </option>
              ))}
            </select>
          </div>

          <div className='filter-group'>
            <label>Sortieren:</label>
            <select value={sortBy} onChange={e => setSortBy(e.target.value)}>
              <option value='newest'>Neueste zuerst</option>
              <option value='oldest'>Älteste zuerst</option>
              <option value='device'>Nach Gerät</option>
              <option value='category'>Nach Kategorie</option>
            </select>
          </div>

          {(filterDevice !== 'all' || filterCategory !== 'all') && (
            <button
              className='btn-reset-filters'
              onClick={() => {
                setFilterDevice('all')
                setFilterCategory('all')
              }}
            >
              <CloseIcon fontSize='small' style={{ marginRight: '0.25rem' }} />
              Filter zurücksetzen
            </button>
          )}
        </div>
      )}

      {filteredKnowledge.length === 0 && pendingKnowledge.length === 0 ? (
        <div className='empty-state'>
          <CheckCircleIcon className='empty-icon' />
          <p>Keine ausstehenden Einreichungen</p>
          <span className='empty-subtitle'>
            Alle Einreichungen wurden bearbeitet
          </span>
        </div>
      ) : filteredKnowledge.length === 0 ? (
        <div className='empty-state'>
          <FilterListIcon className='empty-icon' />
          <p>Keine Einreichungen entsprechen den Filterkriterien</p>
          <button
            className='btn-reset-filters'
            onClick={() => {
              setFilterDevice('all')
              setFilterCategory('all')
            }}
            style={{ marginTop: '1rem' }}
          >
            <CloseIcon fontSize='small' style={{ marginRight: '0.25rem' }} />
            Filter zurücksetzen
          </button>
        </div>
      ) : (
        <div className='moderation-grid'>
          {filteredKnowledge.map(item => (
            <div
              key={item.id}
              className='knowledge-card'
              onClick={() => setSelectedItem(item)}
            >
              <div className='card-header-moderation-page'>
                <div className='category-badge'>{item.category}</div>
                <div className='device-name'>{item.deviceDisplayName || item.deviceName}</div>
              </div>

              <h3 className='problem-title'>{item.problemTitle}</h3>

              {item.errorCode && (
                <div className='error-code'>Code: {item.errorCode}</div>
              )}

              {item.problemDescription && (
                <div className='problem-desc'>
                  <strong>Problem:</strong>
                  <p>{item.problemDescription}</p>
                </div>
              )}

              <div className='solution-preview'>
                <strong>Lösung:</strong>
                <p>{item.solution.substring(0, 200)}...</p>
              </div>

              <div className='metadata'>
                <div className='meta-item'>
                  <span>Schwierigkeit:</span> {item.difficulty}
                </div>
                {item.estimatedTime && (
                  <div className='meta-item'>
                    <span>Dauer:</span> {item.estimatedTime}
                  </div>
                )}
                {item.components?.length > 0 && (
                  <div className='meta-item'>
                    <span>Komponenten:</span> {item.components.join(', ')}
                  </div>
                )}
                {item.toolsRequired?.length > 0 && (
                  <div className='meta-item'>
                    <span>Werkzeuge:</span> {item.toolsRequired.join(', ')}
                  </div>
                )}
              </div>

              {item.images && item.images.length > 0 && (
                <div className='images-preview'>
                  <strong>
                    {item.images.length} Datei
                    {item.images.length > 1 ? 'en' : ''}
                  </strong>
                  <ImageGallery
                    images={item.images}
                    compact={true}
                    maxPreview={3}
                    clickHandler={false}
                  />
                </div>
              )}

              <div className='submission-info'>
                <span>Eingereicht von: {item.submittedBy}</span>
                <span>
                  {item.submittedAt?.toDate().toLocaleDateString('de-DE')}
                </span>
              </div>

              <div className='card-actions'>
                <button
                  className='btn-review'
                  onClick={() => setSelectedItem(item)}
                >
                  <PlayArrowIcon className='btn-icon' />
                  <span>Moderation starten</span>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Detail Modal */}
      {selectedItem && (
        <div className='modal-overlay' onClick={handleCloseModal}>
          <div
            className={`admin-moderation-modal-content ${isEditMode ? 'edit-mode-active' : ''}`}
            onClick={e => e.stopPropagation()}
          >
            {isEditMode && (
              <div className='edit-mode-banner'>
                <EditIcon fontSize='small' style={{ marginRight: '0.5rem' }} />
                Bearbeitungsmodus aktiv - Änderungen werden erst nach dem
                Speichern übernommen
              </div>
            )}
            <div className='modal-header-moderation-page'>
              <h2 style={{ marginBottom: '0' }}>
                {isEditMode ? 'Bearbeiten' : selectedItem.problemTitle}
              </h2>
              <div
                style={{
                  display: 'flex',
                  gap: '0.5rem',
                  alignItems: 'center'
                }}
              >
                <button
                  className='btn-edit'
                  onClick={() => setIsEditMode(!isEditMode)}
                  disabled={processing}
                >
                  {isEditMode ? (
                    <>
                      <VisibilityIcon
                        fontSize='small'
                        style={{ marginRight: '0.25rem' }}
                      />
                      Vorschau
                    </>
                  ) : (
                    <>
                      <EditIcon
                        fontSize='small'
                        style={{ marginRight: '0.25rem' }}
                      />
                      Bearbeiten
                    </>
                  )}
                </button>
                <button className='close-btn' onClick={handleCloseModal}>
                  <CloseIcon />
                </button>
              </div>
            </div>

            <div className='modal-body'>
              {/* Problem Titel */}
              {isEditMode && (
                <div className='detail-section'>
                  <h4>Problem Titel</h4>
                  <input
                    type='text'
                    className='edit-input'
                    value={editedData?.problemTitle || ''}
                    onChange={e =>
                      handleEditChange('problemTitle', e.target.value)
                    }
                  />
                </div>
              )}

              <div className='detail-section'>
                <h4>Gerät & Kategorie</h4>
                <p>
                  <strong>Gerät:</strong> {selectedItem.deviceDisplayName || selectedItem.deviceName}
                </p>
                <p>
                  <strong>Kategorie:</strong> {selectedItem.category}
                </p>
                {isEditMode ? (
                  <div className='form-group'>
                    <label>
                      <strong>Fehlercode:</strong>
                    </label>
                    <input
                      type='text'
                      className='edit-input'
                      value={editedData?.errorCode || ''}
                      onChange={e =>
                        handleEditChange('errorCode', e.target.value)
                      }
                      placeholder='Optional'
                    />
                  </div>
                ) : (
                  selectedItem.errorCode && (
                    <p>
                      <strong>Fehlercode:</strong> {selectedItem.errorCode}
                    </p>
                  )
                )}
              </div>

              <div className='detail-section'>
                <h4>Problembeschreibung</h4>
                {isEditMode ? (
                  <textarea
                    className='edit-textarea'
                    value={editedData?.problemDescription || ''}
                    onChange={e =>
                      handleEditChange('problemDescription', e.target.value)
                    }
                    rows='4'
                    placeholder='Beschreibung des Problems...'
                  />
                ) : (
                  <p>
                    {selectedItem.problemDescription || 'Keine Beschreibung'}
                  </p>
                )}
              </div>

              <div className='detail-section'>
                <h4>Lösung (vollständig)</h4>
                {isEditMode ? (
                  <>
                    <SolutionTextareaWithMentions
                      value={editedData?.solution || ''}
                      onChange={value => handleEditChange('solution', value)}
                      images={editedData?.images || []}
                      rows={8}
                      disabled={processing}
                    />
                    {editedData?.solution && editedData?.images && editedData.images.length > 0 && (
                      <div style={{ marginTop: '0.5rem' }}>
                        <button
                          type='button'
                          onClick={() => setShowSolutionPreview(!showSolutionPreview)}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                            padding: '0.5rem 0.75rem',
                            background: 'transparent',
                            border: '1px solid #d1d5db',
                            borderRadius: '6px',
                            color: '#6b7280',
                            fontSize: '0.875rem',
                            cursor: 'pointer',
                            transition: 'all 0.2s'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = '#f3f4f6'
                            e.currentTarget.style.borderColor = '#9ca3af'
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = 'transparent'
                            e.currentTarget.style.borderColor = '#d1d5db'
                          }}
                          disabled={processing}
                        >
                          {showSolutionPreview ? (
                            <>
                              <VisibilityOffIcon fontSize='small' />
                              Vorschau ausblenden
                            </>
                          ) : (
                            <>
                              <VisibilityIcon fontSize='small' />
                              Vorschau anzeigen
                            </>
                          )}
                        </button>
                        
                        {showSolutionPreview && (
                          <div style={{ marginTop: '0.75rem', padding: '1rem', background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: '8px' }}>
                            <div style={{ fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.5rem', color: '#6b7280' }}>
                              Vorschau:
                            </div>
                            <SolutionWithImageReferences
                              solution={editedData.solution}
                              images={editedData.images || []}
                            />
                          </div>
                        )}
                      </div>
                    )}
                  </>
                ) : (
                  <SolutionWithImageReferences
                    solution={selectedItem.solution}
                    images={selectedItem.images || []}
                  />
                )}
              </div>

              {(selectedItem.images?.length > 0 || isEditMode) && (
                <div className='detail-section'>
                  <h4>Bilder/Videos/PDFs ({editedData?.images?.length || 0})</h4>
                  {isEditMode ? (
                    <div className='image-editor'>
                      {editedData?.images && editedData.images.length > 0 ? (
                        <div className='image-list'>
                          {editedData.images.map((img, idx) => {
                            const isVideo = img.mediaType === 'video' || (img.url || '').match(/\.(mp4|webm|mov)$/i) || (img.filename || img.name || '').match(/\.(mp4|webm|mov)$/i)
                            const isPDF = img.mediaType === 'pdf' || (img.filename || img.name || '').match(/\.pdf$/i) || (img.url || '').match(/\.pdf$/i)
                            return (
                            <div key={idx} className='image-item'>
                              {isPDF ? (
                                <div style={{
                                  width: '150px',
                                  height: '150px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  background: '#f5f5f5',
                                  flexDirection: 'column',
                                  gap: '8px',
                                  border: '1px solid #ddd'
                                }}>
                                  <PictureAsPdf style={{ fontSize: '48px', color: '#d32f2f' }} />
                                  <span style={{ fontSize: '12px', color: '#666' }}>PDF</span>
                                </div>
                              ) : isVideo ? (
                                <video
                                  src={img.url}
                                  controls
                                  preload="metadata"
                                  style={{
                                    maxWidth: '150px',
                                    maxHeight: '150px',
                                    objectFit: 'cover'
                                  }}
                                />
                              ) : (
                                <img
                                  src={img.url}
                                  alt={img.name || `Bild ${idx + 1}`}
                                  style={{
                                    maxWidth: '150px',
                                    maxHeight: '150px'
                                  }}
                                />
                              )}
                              <button
                                className='btn-remove-image'
                                onClick={() => {
                                  const newImages = editedData.images.filter(
                                    (_, i) => i !== idx
                                  )
                                  handleEditChange('images', newImages)
                                }}
                              >
                                <CloseIcon
                                  fontSize='small'
                                  style={{ marginRight: '0.25rem' }}
                                />
                                Entfernen
                              </button>
                            </div>
                            )
                          })}
                        </div>
                      ) : (
                        <p style={{ color: '#999', marginBottom: '1rem' }}>
                          Keine Bilder/Videos/PDFs vorhanden
                        </p>
                      )}
                      <div style={{ marginTop: '1rem' }}>
                        <input
                          type='file'
                          id='image-upload'
                          accept='image/*,video/mp4,video/webm,video/quicktime,application/pdf'
                          multiple
                          onChange={handleImageUpload}
                          style={{ display: 'none' }}
                        />
                        <label htmlFor='image-upload'>
                          <button
                            type='button'
                            className='btn-add-image'
                            onClick={() =>
                              document.getElementById('image-upload').click()
                            }
                            disabled={processing}
                          >
                            <AddPhotoAlternateIcon
                              fontSize='small'
                              style={{ marginRight: '0.25rem' }}
                            />
                            Bilder/Videos/PDFs hinzufügen
                          </button>
                        </label>
                      </div>
                    </div>
                  ) : (
                    <ImageGallery
                      images={selectedItem.images}
                      compact={false}
                    />
                  )}
                </div>
              )}

              <div className='detail-section'>
                <h4>Zusätzliche Informationen</h4>
                {isEditMode ? (
                  <>
                    <div className='form-group'>
                      <label>
                        <strong>Schwierigkeit:</strong>
                      </label>
                      <select
                        className='edit-input'
                        value={editedData?.difficulty || ''}
                        onChange={e =>
                          handleEditChange('difficulty', e.target.value)
                        }
                      >
                        <option value='easy'>Einfach</option>
                        <option value='medium'>Mittel</option>
                        <option value='hard'>Schwer</option>
                      </select>
                    </div>
                    <div className='form-group'>
                      <label>
                        <strong>Geschätzte Dauer:</strong>
                      </label>
                      <input
                        type='text'
                        className='edit-input'
                        value={editedData?.estimatedTime || ''}
                        onChange={e =>
                          handleEditChange('estimatedTime', e.target.value)
                        }
                        placeholder='z.B. 30 Minuten'
                      />
                    </div>
                    <div className='form-group'>
                      <label>
                        <strong>
                          Komponenten: <small>(kommagetrennt)</small>
                        </strong>
                      </label>
                      <input
                        type='text'
                        className='edit-input'
                        value={
                          typeof editedData?.components === 'string'
                            ? editedData.components
                            : editedData?.components?.join(', ') || ''
                        }
                        onChange={e =>
                          handleEditChange('components', e.target.value)
                        }
                        placeholder='z.B. Motor, Pumpe, Ventil'
                      />
                    </div>
                    <div className='form-group'>
                      <label>
                        <strong>
                          Werkzeuge: <small>(kommagetrennt)</small>
                        </strong>
                      </label>
                      <input
                        type='text'
                        className='edit-input'
                        value={
                          typeof editedData?.toolsRequired === 'string'
                            ? editedData.toolsRequired
                            : editedData?.toolsRequired?.join(', ') || ''
                        }
                        onChange={e =>
                          handleEditChange('toolsRequired', e.target.value)
                        }
                        placeholder='z.B. Schraubenzieher, Zange, Multimeter'
                      />
                    </div>
                  </>
                ) : (
                  <>
                    <p>
                      <strong>Schwierigkeit:</strong> {selectedItem.difficulty}
                    </p>
                    {selectedItem.estimatedTime && (
                      <p>
                        <strong>Geschätzte Dauer:</strong>{' '}
                        {selectedItem.estimatedTime}
                      </p>
                    )}
                    {selectedItem.components?.length > 0 && (
                      <p>
                        <strong>Komponenten:</strong>{' '}
                        {selectedItem.components.join(', ')}
                      </p>
                    )}
                    {selectedItem.toolsRequired?.length > 0 && (
                      <p>
                        <strong>Werkzeuge:</strong>{' '}
                        {selectedItem.toolsRequired.join(', ')}
                      </p>
                    )}
                  </>
                )}
              </div>

              <div className='detail-section'>
                <KnowledgeHistory
                  knowledgeId={selectedItem.id}
                  currentUser={user}
                  userRole={user?.role || 'technician'}
                />
              </div>
            </div>

            <div className='modal-actions'>
              {isEditMode ? (
                <div style={{ display: 'flex', gap: '1rem', width: '100%' }}>
                  <button
                    className='btn-secondary'
                    onClick={handleCancelEdit}
                    disabled={processing}
                  >
                    Abbrechen
                  </button>
                  <button
                    className='btn-save'
                    onClick={handleSaveChanges}
                    disabled={processing}
                    style={{ flex: 1 }}
                  >
                    {processing ? (
                      'Speichere...'
                    ) : (
                      <>
                        <SaveIcon
                          fontSize='small'
                          style={{ marginRight: '0.25rem' }}
                        />
                        Änderungen speichern
                      </>
                    )}
                  </button>
                </div>
              ) : (
                <>
                  <div className='rejection-section'>
                    <input
                      type='text'
                      placeholder='Ablehnungsgrund (optional)'
                      value={rejectionReason}
                      onChange={e => setRejectionReason(e.target.value)}
                      disabled={processing}
                    />
                    <button
                      className='btn-reject'
                      onClick={() => handleReject(selectedItem)}
                      disabled={processing}
                    >
                      Ablehnen
                    </button>
                  </div>

                  <button
                    className='btn-approve'
                    onClick={() => handleApprove(selectedItem)}
                    disabled={processing}
                  >
                    {processing ? (
                      'Verarbeite...'
                    ) : (
                      <>
                        <CheckCircleIcon className='btn-icon' />
                        <span>Freigeben & Einbetten</span>
                      </>
                    )}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Confirm Modal */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={() =>
          setConfirmModal({ ...confirmModal, isOpen: false, onConfirm: null })
        }
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
        type={confirmModal.type}
      />
    </div>
  )
}

export default AdminModerationPage
